<template>
    <div>
        <v-card :loading="loading" flat>
            <v-card-title class="d-flex justify-space-between align-center">
                <span class="text-h6 font-weight-bold">Materials</span>
                <v-btn color="primary" rounded @click="openMaterialDialog">
                    <v-icon left>mdi-plus</v-icon>
                    New Material
                </v-btn>
            </v-card-title>

            <v-card-text>
                <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search Input"
                    single-line
                    hide-details
                    class="mb-4"
                />

                <!-- Scrollable Materials List -->
                <div
                    ref="scrollContainer"
                    class="overflow-y-auto"
                    style="max-height: 65vh; padding-right: 8px;"
                    @scroll.passive="onScroll"
                >
                    <v-expansion-panels
                        v-model="activePanel"
                        accordion
                        focusable
                    >
                        <v-expansion-panel
                            v-for="material in filteredMaterials"
                            :key="material.id"
                            :value="material.id"
                        >
                            <v-expansion-panel-header class="grey lighten-3">
                                <div
                                    class="d-flex justify-space-between align-center w-100"
                                >
                                    <div class="font-weight-bold">
                                        {{ material.name }} ({{
                                            material.abbreviation
                                        }})
                                    </div>
                                    <div class="d-flex align-center">
                                        <v-btn
                                            icon
                                            small
                                            :disabled="
                                                isAnyMaterialBeingEdited &&
                                                    !editingMaterials[
                                                        material.id
                                                    ]
                                            "
                                            @click.stop="toggleEdit(material)"
                                        >
                                            <v-icon small>mdi-pencil</v-icon>
                                        </v-btn>
                                        <v-btn
                                            icon
                                            small
                                            :disabled="
                                                isAnyMaterialBeingEdited &&
                                                    !editingMaterials[
                                                        material.id
                                                    ]
                                            "
                                            @click.stop="
                                                deleteMaterial(material)
                                            "
                                        >
                                            <v-icon small>mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </v-expansion-panel-header>

                            <v-expansion-panel-content class="">
                                <!-- Action Buttons -->
                                <div
                                    v-if="editingMaterials[material.id]"
                                    class="d-flex justify-space-between align-center mt-3 pb-3"
                                    style="position: sticky; top: 0; z-index: 10; background: white;"
                                >
                                    <v-btn
                                        color="primary"
                                        outlined
                                        small
                                        @click="openUrl(material.url)"
                                        :disabled="
                                            !material.url ||
                                                !isValidUrl(material.url)
                                        "
                                    >
                                        <v-icon left small
                                            >mdi-open-in-new</v-icon
                                        >
                                        Open Material Link
                                    </v-btn>
                                    <div class="d-flex align-center">
                                        <v-btn
                                            small
                                            text
                                            color="secondary"
                                            class="mr-2"
                                            @click="
                                                $set(
                                                    editingMaterials,
                                                    material.id,
                                                    false
                                                )
                                            "
                                        >
                                            <v-icon left small
                                                >mdi-close</v-icon
                                            >
                                            Cancel
                                        </v-btn>
                                        <v-btn
                                            rounded
                                            color="primary"
                                            @click="updateMaterial(material)"
                                            :loading="loading"
                                        >
                                            <v-icon left small
                                                >mdi-content-save</v-icon
                                            >
                                            Save Changes
                                        </v-btn>
                                    </div>
                                </div>

                                <!-- Content -->
                                <div>
                                    <v-btn
                                        v-if="!editingMaterials[material.id]"
                                        class="mt-3"
                                        color="primary"
                                        outlined
                                        small
                                        @click="openUrl(material.url)"
                                        :disabled="
                                            !material.url ||
                                                !isValidUrl(material.url)
                                        "
                                    >
                                        <v-icon left small
                                            >mdi-open-in-new</v-icon
                                        >
                                        Open Material Link
                                    </v-btn>
                                    <v-data-table
                                        v-if="!editingMaterials[material.id]"
                                        :headers="attributeHeaders"
                                        :items="material.attributes"
                                        class="mt-3"
                                        dense
                                        hide-default-footer
                                        :items-per-page="-1"
                                    >
                                        <template v-slot:item.name="{ item }">
                                            <span class="font-weight-medium">{{
                                                item.name
                                            }}</span>
                                        </template>

                                        <template
                                            v-slot:item.abbreviation="{ item }"
                                        >
                                            <span class="text--secondary">{{
                                                item.abbreviation || '-'
                                            }}</span>
                                        </template>

                                        <template
                                            v-slot:item.options="{ item }"
                                        >
                                            <div class="d-flex flex-wrap">
                                                <v-chip
                                                    v-for="(opt,
                                                    optIndex) in item.options"
                                                    :key="optIndex"
                                                    small
                                                    class="ma-1"
                                                    color="grey darken-1"
                                                    outlined
                                                >
                                                    {{ opt.label }} ({{
                                                        opt.code
                                                    }})
                                                </v-chip>
                                            </div>
                                        </template>
                                        <template
                                            v-slot:[`item.new`]="{ item }"
                                        >
                                            <v-icon
                                                :color="
                                                    item.new ? 'green' : 'grey'
                                                "
                                                small
                                            >
                                                {{
                                                    item.new
                                                        ? 'mdi-checkbox-marked'
                                                        : 'mdi-checkbox-blank-outline'
                                                }}
                                            </v-icon>
                                        </template>
                                    </v-data-table>

                                    <div v-else class="mt-4">
                                        <div>
                                            <v-row>
                                                <v-col cols="6">
                                                    <v-text-field
                                                        v-model="material.name"
                                                        label="Material Name"
                                                        outlined
                                                        dense
                                                        required
                                                    />
                                                </v-col>
                                                <v-col cols="6">
                                                    <v-text-field
                                                        v-model="
                                                            material.abbreviation
                                                        "
                                                        label="Abbreviation"
                                                        outlined
                                                        dense
                                                    />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field
                                                        v-model="material.url"
                                                        label="Material URL"
                                                        outlined
                                                        dense
                                                        placeholder="https://example.com"
                                                    />
                                                    <v-textarea
                                                        v-model="
                                                            material.description
                                                        "
                                                        label="Description"
                                                        rows="2"
                                                        outlined
                                                        dense
                                                    />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <!-- Add Attribute -->
                                        <div class="mb-4">
                                            <v-btn
                                                class="mr-3"
                                                color="primary"
                                                @click="addAttribute(material)"
                                            >
                                                Add Attribute
                                            </v-btn>
                                            <!-- Attributes Update File -->
                                            <v-btn
                                                color="primary"
                                                @click="
                                                    triggerAttributesUpload(
                                                        material.id
                                                    )
                                                "
                                            >
                                                <v-icon left small
                                                    >mdi-file-excel</v-icon
                                                >
                                                Upload Attributes
                                            </v-btn>
                                            <v-btn
                                                class="ml-3"
                                                color="primary"
                                                outlined
                                                @click="
                                                    downloadAttributesTemplate(
                                                        material
                                                    )
                                                "
                                            >
                                                <v-icon left small
                                                    >mdi-download</v-icon
                                                >
                                                Download Template
                                            </v-btn>
                                            <input
                                                type="file"
                                                :ref="
                                                    `attributesInput_${material.id}`
                                                "
                                                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                                style="display:none"
                                                @change="
                                                    e =>
                                                        onAttributesFilePicked(
                                                            e.target.files[0],
                                                            material
                                                        )
                                                "
                                            />
                                        </div>
                                        <div
                                            v-for="(attr,
                                            attrIndex) in material.attributes"
                                            :key="attrIndex"
                                            class="mb-4 pa-3 grey lighten-4 rounded"
                                        >
                                            <v-text-field
                                                v-model="attr.name"
                                                label="Attribute Name"
                                                outlined
                                                dense
                                                class="mb-2"
                                                @input="
                                                    onAttributeNameChange(attr)
                                                "
                                            />
                                            <v-row class="mb-2" align="center">
                                                <v-col cols="6">
                                                    <v-text-field
                                                        v-model="
                                                            attr.abbreviation
                                                        "
                                                        label="Attribute Abbreviation"
                                                        outlined
                                                        dense
                                                    />
                                                </v-col>
                                                <v-col
                                                    cols="6"
                                                    class="d-flex align-center"
                                                >
                                                    <v-switch
                                                        v-model="attr.new"
                                                        label=" Users can add new options"
                                                        dense
                                                        hide-details
                                                        class="my-0 py-0 attribute-new-switch"
                                                    />
                                                </v-col>
                                            </v-row>

                                            <div
                                                v-for="(opt,
                                                optIndex) in attr.options"
                                                :key="optIndex"
                                                class="d-flex align-center mb-2"
                                            >
                                                <v-text-field
                                                    v-model="opt.label"
                                                    label="Option Label"
                                                    class="mr-2"
                                                    dense
                                                    outlined
                                                />
                                                <v-text-field
                                                    v-model="opt.code"
                                                    label="Option Code"
                                                    class="mr-2"
                                                    dense
                                                    outlined
                                                />
                                                <v-btn
                                                    icon
                                                    @click="
                                                        removeOption(
                                                            material,
                                                            attrIndex,
                                                            optIndex
                                                        )
                                                    "
                                                    class="mt-0"
                                                    style="align-self: flex-start;"
                                                >
                                                    <v-icon color="red"
                                                        >mdi-delete</v-icon
                                                    >
                                                </v-btn>
                                            </div>

                                            <div
                                                class="d-flex justify-space-between align-center mt-2"
                                            >
                                                <v-btn
                                                    small
                                                    color="primary"
                                                    @click="
                                                        addOption(
                                                            material,
                                                            attrIndex
                                                        )
                                                    "
                                                >
                                                    <v-icon left small
                                                        >mdi-plus</v-icon
                                                    >
                                                    Add Option
                                                </v-btn>
                                                <v-btn
                                                    small
                                                    color="red"
                                                    class="white--text"
                                                    @click="
                                                        openDeleteAttributeDialog(
                                                            material,
                                                            attrIndex
                                                        )
                                                    "
                                                >
                                                    Delete Attribute
                                                </v-btn>
                                            </div>
                                            <v-divider class="my-3" />
                                        </div>
                                    </div>
                                </div>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </div>
            </v-card-text>
        </v-card>

        <v-dialog
            v-if="selectedMaterial"
            v-model="materialDialog"
            max-width="500px"
            persistent
        >
            <v-card>
                <v-card-title>
                    <span class="text-h6">
                        New Material
                    </span>
                </v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="selectedMaterial.name"
                        label="Material Name"
                        required
                    />
                    <v-text-field
                        v-model="selectedMaterial.abbreviation"
                        label="Abbreviation"
                        required
                    />
                    <v-textarea
                        v-model="selectedMaterial.description"
                        label="Description"
                        rows="2"
                    />
                    <v-text-field
                        v-model="selectedMaterial.url"
                        label="Material URL"
                        placeholder="https://example.com"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn text @click="closeDialog">Cancel</v-btn>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        @click="createMaterial()"
                        :loading="loading"
                        :disabled="
                            loading ||
                                !selectedMaterial.name ||
                                !selectedMaterial.abbreviation
                        "
                    >
                        Create Material
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-snackbar
            v-model="errorSnackbar"
            :timeout="3500"
            :color="errorMsg.includes('successfully') ? 'green' : 'red'"
            bottom
            right
        >
            {{ errorMsg }}
        </v-snackbar>

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title class="text-h6">
                    Confirm Delete
                </v-card-title>
                <v-card-text>
                    <p>
                        Are you sure you want to delete the material
                        <strong>"{{ materialToDelete?.name }}"</strong>?
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-btn text @click="closeDeleteDialog">
                        Cancel
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="red"
                        text
                        @click="confirmDelete"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Attribute Confirmation Dialog -->
        <v-dialog v-model="deleteAttributeDialog" max-width="400px" persistent>
            <v-card>
                <v-card-title class="text-h6">
                    Confirm Delete Attribute
                </v-card-title>
                <v-card-text>
                    <p>
                        Are you sure you want to delete the attribute
                        <strong>"{{ attributeToDelete?.name }}"</strong>?
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-btn text @click="closeDeleteAttributeDialog">
                        Cancel
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="red"
                        text
                        @click="confirmDeleteAttribute"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import API from '@/services/api'
import * as XLSX from 'xlsx'
import { v4 as uuidv4 } from 'uuid'
import { mapMutations } from 'vuex'
export default {
    name: 'MaterialSettings',
    data() {
        return {
            company: JSON.parse(localStorage.getItem('company')),
            loading: false,
            search: '',
            materialDialog: false,
            selectedMaterial: null,
            itemsVisibleCount: 10,
            chunkSize: 10,
            scrollTriggerDistance: 150,
            materialsRaw: [],
            materialsProcessed: [],
            editingMaterials: {},
            errorSnackbar: false,
            errorMsg: '',
            activePanel: null,
            deleteDialog: false,
            materialToDelete: null,
            deleteAttributeDialog: false,
            attributeToDelete: null,
            materialForAttributeDelete: null,
            attributeHeaders: [
                {
                    text: 'Attribute',
                    value: 'name',
                    sortable: false,
                    width: '20%',
                },
                {
                    text: 'Abbreviation',
                    value: 'abbreviation',
                    sortable: false,
                    width: '20%',
                },
                {
                    text: 'Options',
                    value: 'options',
                    sortable: false,
                    width: '55%',
                },
                {
                    text: 'New',
                    value: 'new',
                    sortable: false,
                    width: '5%',
                },
            ],
        }
    },

    computed: {
        filteredMaterials() {
            return (this.materialsProcessed || []).filter(material => {
                const searchTerm = this.search.toLowerCase()
                const materialName = (material.name || '').toLowerCase()
                const materialAbbreviation = (
                    material.abbreviation || ''
                ).toLowerCase()

                return (
                    materialName.includes(searchTerm) ||
                    materialAbbreviation.includes(searchTerm)
                )
            })
        },
        isAnyMaterialBeingEdited() {
            return this.filteredMaterials.some(
                material => this.editingMaterials[material.id]
            )
        },
    },
    watch: {
        activePanel(newPanel, oldPanel) {
            if (
                newPanel !== oldPanel &&
                this.isAnyMaterialBeingEdited &&
                newPanel
            ) {
                if (!this.editingMaterials[newPanel]) {
                    this.closeAllEditing()
                }
            }
        },
        search() {
            // Reset activePanel if the currently active material is not visible after filtering
            if (this.activePanel) {
                const isActiveMaterialVisible = this.filteredMaterials.some(
                    material => material.id === this.activePanel
                )
                if (!isActiveMaterialVisible) {
                    this.activePanel = null
                }
            }
        },
    },
    async mounted() {
        try {
            this.loading = true
            await this.fetchMaterials()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        onScroll() {
            const el = this.$refs.scrollContainer
            if (!el) return
            const nearBottom =
                el.scrollTop + el.clientHeight >=
                el.scrollHeight - this.scrollTriggerDistance
            if (
                nearBottom &&
                this.itemsVisibleCount < this.filteredMaterials.length
            ) {
                this.itemsVisibleCount += this.chunkSize
            }
        },

        openMaterialDialog() {
            this.selectedMaterial = {
                name: '',
                description: '',
                abbreviation: '',
                url: '',
                attributes: {},
            }
            this.materialDialog = true
        },

        closeDialog() {
            this.materialDialog = false
            this.selectedMaterial = null
        },

        deleteMaterial(material) {
            this.materialToDelete = material
            this.deleteDialog = true
        },

        confirmDelete() {
            if (this.materialToDelete) {
                this.executeDeleteMaterial(this.materialToDelete)
                this.closeDeleteDialog()
            }
        },

        closeDeleteDialog() {
            this.deleteDialog = false
            this.materialToDelete = null
        },

        async executeDeleteMaterial(material) {
            try {
                this.loading = true

                await API.deleteMaterial(material.id)

                const index = this.materialsRaw.findIndex(
                    m => m.id === material.id
                )
                if (index >= 0) {
                    this.materialsRaw.splice(index, 1)
                    this.processMaterials()
                    this.showSuccess('Material deleted successfully')
                }
            } catch (e) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: e.message,
                })
            } finally {
                this.loading = false
            }
        },

        async fetchMaterials() {
            this.loading = true
            try {
                const materials = await API.getMaterials()

                if (materials) {
                    this.materialsRaw = Object.values(materials)
                } else {
                    this.materialsRaw = []
                }

                // format materials
                this.processMaterials()
            } catch (e) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: e.message,
                })
            } finally {
                this.loading = false
            }
        },

        processMaterials() {
            this.materialsProcessed = this.materialsRaw.map(material => ({
                ...material,
                attributes: this.convertAttributesToArray(
                    material.attributes || {}
                ),
            }))
        },

        toggleEdit(material) {
            this.$set(this.editingMaterials, material.id, true)
        },

        closeAllEditing() {
            Object.keys(this.editingMaterials).forEach(materialId => {
                this.$set(this.editingMaterials, materialId, false)
            })
        },

        async updateMaterial(material) {
            try {
                // validate material
                if (!this.validateMaterial(material)) return

                this.loading = true

                // convert attributes to object structure for storage
                const materialData = {
                    ...material,
                    attributes: this.convertAttributesToObject(
                        material.attributes || {}
                    ),
                }

                // update material using the API endpoint
                const response = await API.updateMaterial(materialData)

                const index = this.materialsRaw.findIndex(
                    m => m.id === material.id
                )
                if (index !== -1) {
                    this.materialsRaw.splice(index, 1, response.data.material)
                    this.processMaterials()
                    this.$set(this.editingMaterials, material.id, false)
                    this.showSuccess('Material updated successfully')
                }
            } catch (e) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: e.message,
                })
            } finally {
                this.loading = false
            }
        },

        async createMaterial() {
            try {
                // validate material
                if (!this.validateMaterial(this.selectedMaterial)) return

                this.loading = true

                // convert attributes to object structure for storage
                const materialData = {
                    ...this.selectedMaterial,
                    attributes: this.convertAttributesToObject(
                        this.selectedMaterial.attributes || {}
                    ),
                }

                const response = await API.createMaterial(materialData)

                // add the new material to raw data
                this.materialsRaw.push(response.data.material)
                this.processMaterials()

                this.closeDialog()
                this.showSuccess('Material created successfully')
            } catch (e) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: e.message,
                })
            } finally {
                this.loading = false
            }
        },

        validateMaterial(material) {
            if (!material.name || !material.name.trim()) {
                this.showError('Material Name is required.')
                return false
            }
            if (!material.abbreviation || !material.abbreviation.trim()) {
                this.showError('Abbreviation is required.')
                return false
            }

            // Convert attributes to array for validation
            const attributesArray = this.convertAttributesToArray(
                material.attributes || {}
            )

            for (const attr of attributesArray) {
                if (!attr.name || !attr.name.trim()) {
                    this.showError('All attributes must have a name.')
                    return false
                }
                for (const opt of attr.options) {
                    if (
                        !opt.label ||
                        !opt.label.trim() ||
                        !opt.code ||
                        !opt.code.trim()
                    ) {
                        this.showError(
                            'All options must have both label and code.'
                        )
                        return false
                    }
                }
            }
            return true
        },

        showError(msg) {
            this.errorMsg = msg
            this.errorSnackbar = true
        },

        showSuccess(msg) {
            this.errorMsg = msg
            this.errorSnackbar = true
        },

        triggerAttributesUpload(materialId) {
            const refName = `attributesInput_${materialId}`
            let el = this.$refs[refName]
            if (Array.isArray(el)) el = el[0]
            if (el && el.click) {
                el.click()
            }
        },

        downloadAttributesTemplate(material) {
            try {
                const worksheetData = [
                    ['Attribute Name', 'Code', 'Second Attribute', 'Code'],
                ]

                const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
                const workbook = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(
                    workbook,
                    worksheet,
                    'AttributesTemplate'
                )

                const materialName = material?.name
                    ? material.name.trim().replace(/\s+/g, '_')
                    : 'material'
                const fileName = `${materialName}_attributes_template.xlsx`
                XLSX.writeFile(workbook, fileName)
            } catch (error) {
                this.showError('Error downloading attributes template')
            }
        },

        async onAttributesFilePicked(file, material) {
            if (!file) return

            // validate xlsx file
            const validTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                '.xlsx',
            ]
            const isValidType = validTypes.some(
                type =>
                    file.type === type || file.name.toLowerCase().endsWith(type)
            )

            if (!isValidType) {
                this.showError('You can only upload XLSX files')
                return
            }

            try {
                this.loading = true

                // Process File
                const attributes = await this.processXLSXFile(file)

                if (attributes && attributes.length > 0) {
                    material.attributes = attributes
                } else {
                    this.showError('The file does not contain valid attributes')
                }
            } catch (error) {
                this.showError('Error processing XLSX file')
            } finally {
                this.loading = false
            }
        },

        addAttribute(material) {
            if (!material.attributes) {
                material.attributes = []
            }
            material.attributes.push({
                id: uuidv4(),
                name: '',
                abbreviation: '',
                new: false,
                options: [],
            })
            this.$set(this.editingMaterials, material.id, true)
        },

        addOption(material, attrIndex) {
            const attr = material.attributes[attrIndex]
            if (!attr.options) {
                attr.options = []
            }
            attr.options.push({
                id: uuidv4(),
                label: '',
                code: '',
            })
        },

        removeOption(material, attrIndex, optIndex) {
            material.attributes[attrIndex].options.splice(optIndex, 1)
        },

        openDeleteAttributeDialog(material, attrIndex) {
            this.materialForAttributeDelete = material
            this.attributeToDelete = material.attributes[attrIndex]
            this.deleteAttributeDialog = true
        },

        confirmDeleteAttribute() {
            if (this.materialForAttributeDelete && this.attributeToDelete) {
                this.executeDeleteAttribute(
                    this.materialForAttributeDelete,
                    this.attributeToDelete
                )
                this.closeDeleteAttributeDialog()
            }
        },

        closeDeleteAttributeDialog() {
            this.deleteAttributeDialog = false
            this.attributeToDelete = null
            this.materialForAttributeDelete = null
        },

        executeDeleteAttribute(material, attribute) {
            const attrIndex = material.attributes.findIndex(
                attr => attr.id === attribute.id
            )
            if (attrIndex !== -1) {
                material.attributes.splice(attrIndex, 1)
            }
        },

        async processXLSXFile(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()

                reader.onload = event => {
                    try {
                        const attributes = this.parseExcelData(
                            event.target.result
                        )
                        resolve(attributes)
                    } catch (error) {
                        reject(
                            new Error(`Error processing file: ${error.message}`)
                        )
                    }
                }

                reader.onerror = () => {
                    reject(new Error('Error reading file'))
                }

                reader.readAsArrayBuffer(file)
            })
        },

        parseExcelData(arrayBuffer) {
            // Read Excel file and convert to array format
            const data = new Uint8Array(arrayBuffer)
            const workbook = XLSX.read(data, { type: 'array' })
            const firstSheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[firstSheetName]

            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
                defval: '',
            })

            if (jsonData.length === 0) {
                throw new Error('The file is empty')
            }

            return this.extractAttributesFromData(jsonData)
        },

        extractAttributesFromData(jsonData) {
            const attributes = []

            // Process pairs of columns: attribute name and option codes
            for (
                let columnIndex = 0;
                columnIndex < jsonData[0].length;
                columnIndex += 2
            ) {
                const attributeName = jsonData[0][columnIndex]
                const optionCodesColumn = columnIndex + 1

                if (this.isEmptyValue(attributeName)) continue // avoid empty columns

                // Check if there's a corresponding option codes column
                if (optionCodesColumn >= jsonData[0].length) break

                const options = this.extractOptionsFromColumnPair(
                    jsonData,
                    columnIndex,
                    optionCodesColumn
                )

                if (options.length > 0) {
                    const attributeNameStr = attributeName.toString().trim()
                    attributes.push({
                        id: uuidv4(),
                        name: attributeNameStr,
                        abbreviation: this.generateAttributeAbbreviation(
                            attributeNameStr
                        ),
                        new: false,
                        options: options,
                    })
                }
            }

            return attributes
        },

        extractOptionsFromColumnPair(
            jsonData,
            attributeColumnIndex,
            codesColumnIndex
        ) {
            const options = []

            // Skip first row (attribute names) and process data rows
            for (let rowIndex = 1; rowIndex < jsonData.length; rowIndex++) {
                const optionLabel = jsonData[rowIndex][attributeColumnIndex]
                const optionCode = jsonData[rowIndex][codesColumnIndex]

                // Both label and code must be present
                if (
                    !this.isEmptyValue(optionLabel) &&
                    !this.isEmptyValue(optionCode)
                ) {
                    options.push({
                        id: uuidv4(),
                        label: optionLabel.toString().trim(),
                        code: optionCode.toString().trim(),
                    })
                }
            }

            return options
        },

        generateAttributeAbbreviation(attributeName) {
            return attributeName
                .toLowerCase()
                .replace(/\s+/g, '')
                .substring(0, 3)
        },

        onAttributeNameChange(attr) {
            if (attr.name && attr.name.trim()) {
                attr.abbreviation = this.generateAttributeAbbreviation(
                    attr.name
                )
            }
        },

        openUrl(url) {
            if (this.isValidUrl(url)) {
                window.open(url, '_blank')
            }
        },

        isValidUrl(string) {
            try {
                new URL(string)
                return true
            } catch (_) {
                return false
            }
        },

        isEmptyValue(value) {
            return !value || value.toString().trim() === ''
        },

        // Convert attributes from object to array for display
        convertAttributesToArray(attributesObj) {
            return Object.keys(attributesObj).map(attrId => ({
                id: attrId,
                ...attributesObj[attrId],
                new: Boolean(attributesObj[attrId].new),
                options: this.convertOptionsToArray(
                    attributesObj[attrId].options || {}
                ),
            }))
        },

        // Convert options from object to array for display
        convertOptionsToArray(optionsObj) {
            return Object.keys(optionsObj).map(optId => ({
                id: optId,
                ...optionsObj[optId],
            }))
        },

        // Convert attributes from array to object for storage
        convertAttributesToObject(attributes) {
            // If it's already an object, return it as is
            if (
                attributes &&
                typeof attributes === 'object' &&
                !Array.isArray(attributes)
            ) {
                return attributes
            }

            // If it's an array, convert to object
            const attributesObj = {}
            const attributesArray = Array.isArray(attributes) ? attributes : []
            attributesArray.forEach(attr => {
                const attrId = attr.id || uuidv4()
                attributesObj[attrId] = {
                    id: attrId,
                    name: attr.name,
                    abbreviation: attr.abbreviation || '',
                    new: Boolean(attr.new),
                    options: this.convertOptionsToObject(attr.options || []),
                }
            })
            return attributesObj
        },

        // Convert options from array to object for storage
        convertOptionsToObject(options) {
            // If it's already an object, return it as is
            if (
                options &&
                typeof options === 'object' &&
                !Array.isArray(options)
            ) {
                return options
            }

            // If it's an array, convert to object
            const optionsObj = {}
            const optionsArray = Array.isArray(options) ? options : []
            optionsArray.forEach(opt => {
                const optId = opt.id || uuidv4()
                optionsObj[optId] = {
                    id: optId,
                    label: opt.label,
                    code: opt.code,
                }
            })
            return optionsObj
        },
    },
}
</script>

<style scoped>
.panel-content-full-width {
    width: 100%;
    padding-left: 0;
    padding-right: 0;
    box-sizing: border-box;
}

.attribute-new-switch {
    margin-top: 0 !important;
}
</style>
